import {useEffect, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import type {AnyAction} from "redux";
import type {ThunkDispatch} from "redux-thunk";
import {DETAILS_VIEW_TYPE, LOADING_STATUS} from "utils/constants";
import {handleNPKeyNavigation} from "utils/keyPressHandler.function";
import * as reduxSelectors from "components/store/application.reducers";
import {taskAction} from "components/mytasks/action/taskAction.action";
import {navigateBetweenTasks} from "components/mytasks/action/navigateBetweenTasks.action";
import {
    removeSelectedDocument, getTaskDetails, handleAddTasksDraftComments, getTaskCommentsByUid,
} from "components/taskdetails-remake/taskDetails.action";
import {PATH_TO_MY_TASKS} from "components/router/paths.js";
import {Loader, LoaderSize} from "components/common/Loader.component";
import {PanelErrorFallback} from "components/errorpage/PanelErrorBoundary";
import TaskDetails from "./TaskDetails.component";
import {getActionsForTask, getTaskNavigation} from "./taskDetailsMasterLayer.function";
import type {TaskDetailsMasterLayerProps} from "types/taskDetailsMasterLayer";

/**
 * The task details page of /task/:taskId, behind fe-task-details-redesign. The task is read from the
 * My tasks list, and its details are loaded on mount and again for every task moved to.
 */
export const TaskDetailsMasterLayer = (props: TaskDetailsMasterLayerProps) => {
    const tasks = useSelector(reduxSelectors.getTasksForRender);
    const taskDetails = useSelector(reduxSelectors.getDetails);
    const oneHandleOnly = useSelector(reduxSelectors.getOneHandleOnlyFlag);
    const attachments = useSelector(reduxSelectors.getTaskAttachments);
    const loadingStatus = useSelector(reduxSelectors.getAttachmentsLoadingStatus);
    const tasksWithPayment = useSelector(reduxSelectors.getTasksWithPayment);
    // typed for thunks, so navigateBetweenTasks hands back the path it computes
    const dispatch = useDispatch<ThunkDispatch<unknown, unknown, AnyAction>>();
    const keyPressRef = useRef<(e: KeyboardEvent) => void>(() => {});

    const taskId = props.match.params.taskId;
    const task = (tasks.allTasks || []).find((x: any) => x.task.key === taskId)?.task ?? null;
    // the searched task is no longer in the task list
    const isTaskMissing = tasks.allTasks?.length > 0 && !task;

    useEffect(() => {
        // the page stays mounted between tasks, so the listener must read the latest task id, not the one it was added with
        const onKeyDown = (e: KeyboardEvent) => keyPressRef.current(e);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
        };
    }, []);

    // this also runs on mount, so it is the only place the details are requested
    const isFirstRequestRef = useRef(true);
    useEffect(() => {
        const isFirstRequest = isFirstRequestRef.current;
        isFirstRequestRef.current = false;
        // awaited in a try, so a request that throws before it is sent is caught as well as one that fails
        const requestData = async () => {
            try {
                await dispatch(getTaskDetails(taskId));
            } catch (error) {
                if (isFirstRequest) props.history("/");
                return;
            }
            if (isFirstRequest) dispatch(removeSelectedDocument());
        };
        requestData();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- requested again only for another task
    }, [taskId]);

    useEffect(() => {
        if (isTaskMissing) {
            props.history(PATH_TO_MY_TASKS);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the task drops out of the list
    }, [isTaskMissing]);

    const taskNavigation = (key: string, isForward: boolean) => {
        props.history(dispatch(navigateBetweenTasks(key, isForward)));
    };

    keyPressRef.current = (e: KeyboardEvent) => {
        handleNPKeyNavigation(e, taskId, taskNavigation);
    };

    const dispatchTaskAction = (type: any, taskIds = [], options = {}) => {
        dispatch(taskAction(type, taskIds, options));
    };

    const dispatchHandleAddTasksDraftComments = (taskKey: string, comment: string, documentUid: string, uid: string) => {
        dispatch(handleAddTasksDraftComments(taskKey, comment, documentUid, uid));
    };

    // the panel passes (uid, taskId, skipLoading), the task comments endpoint only needs the uid
    const dispatchGetTaskCommentsByUid = (uid: string, _taskId: string, skipLoading = false) => {
        dispatch(getTaskCommentsByUid(uid, skipLoading));
    };

    // a failed load leaves no task to render, so the spinner below would never resolve (AC-12773)
    if (loadingStatus === LOADING_STATUS.ERROR)
        return <PanelErrorFallback/>;

    if (!task) {
        return <div className="position-fixed top-50 w-100">
            <Loader size={LoaderSize.Large}/>
        </div>;
    }

    const navigation = getTaskNavigation(task, tasks, oneHandleOnly);
    const activeActions = getActionsForTask(task, navigation, taskDetails, tasksWithPayment);

    return (
        <TaskDetails params={{taskId}}
                     task={task}
                     taskAction={dispatchTaskAction}
                     navigation={navigation}
                     attachments={attachments}
                     taskDetails={taskDetails}
                     loadingStatus={loadingStatus}
                     route={props.route}
                     activeActions={activeActions}
                     navigateBetweenTasks={(key: string, isForward: boolean) => dispatch(navigateBetweenTasks(key, isForward))}
                     navigateBackPath={PATH_TO_MY_TASKS}
                     navigateBack={() => {
                     }}
                     type={DETAILS_VIEW_TYPE.TASK}
                     routerBlocker={props.blocker}
                     allowActionlessComments={taskDetails?.taskPermission?.allowExtraComments}
                     addCommentHandler={dispatchHandleAddTasksDraftComments}
                     getCommentsHandler={dispatchGetTaskCommentsByUid}/>
    );
};

export default TaskDetailsMasterLayer;
